import { useState, useEffect } from 'react';
import { startOfWeek } from 'date-fns';
import { 
  BacklogHeader,
  BacklogStats,
  BacklogKanban,
  BacklogItemForm,
  BacklogItemDetail,
} from '@/components/it-backlog';
import { 
  useItBacklogItems, 
  useItBacklogStats 
} from '@/hooks/useItBacklog';
import { ItBacklogItem } from '@/types/itBacklog';
import { Loader2 } from 'lucide-react';

export default function ItBacklog() {
  const [currentWeek, setCurrentWeek] = useState(() => 
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [showForm, setShowForm] = useState(false);
  const [selectedItem, setSelectedItem] = useState<ItBacklogItem | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [editingItem, setEditingItem] = useState<ItBacklogItem | null>(null);
  
  const { data: items, isLoading: itemsLoading } = useItBacklogItems();
  const { data: stats, isLoading: statsLoading } = useItBacklogStats(currentWeek);
  
  const handleItemClick = (item: ItBacklogItem) => {
    setSelectedItem(item);
    setShowDetail(true);
  };
  
  const handleEdit = () => {
    if (selectedItem) {
      setEditingItem(selectedItem);
      setShowDetail(false);
      setShowForm(true);
    }
  };
  
  const handleFormClose = (open: boolean) => {
    setShowForm(open);
    if (!open) {
      setEditingItem(null);
    }
  };
  
  if (itemsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full p-6 gap-4">
      {/* Header with Week Navigation */}
      <BacklogHeader 
        onCreateClick={() => setShowForm(true)}
        currentWeek={currentWeek}
        onWeekChange={setCurrentWeek}
      />
      
      {/* Stats */}
      <BacklogStats stats={stats} isLoading={statsLoading} />
      
      {/* Kanban Board - flex-1 para ocupar espaço restante */}
      <div className="flex-1 min-h-0">
        <BacklogKanban 
          items={items || []} 
          currentWeek={currentWeek}
          onItemClick={handleItemClick}
        />
      </div>
      
      {/* Form Modal */}
      <BacklogItemForm 
        open={showForm} 
        onOpenChange={handleFormClose}
        item={editingItem}
      />
      
      {/* Detail Sheet */}
      <BacklogItemDetail
        item={selectedItem}
        open={showDetail}
        onOpenChange={setShowDetail}
        onEdit={handleEdit}
      />
    </div>
  );
}
